import { ExclamationCircleFilled, SyncOutlined } from "@ant-design/icons";
import { faRocket, faThLarge, faUsers } from "@fortawesome/free-solid-svg-icons";
import { Alert, Button, Card, Col, Row, Space, Spin, Tooltip, Typography } from "antd";
import React, { useState } from "react";
import { useDataProvider, useEditContext, useNotify, usePermissions, useQuery } from "react-admin";
import { ExtendedDataProvider, ServiceItemDetailProps, RoutableTabConfig } from '@src/types';
import { HealthCheckResponsesColumns } from "../Common/Columns";
import ResponseListView from "../Common/HTTPResponseList";
import { getTagForChangeState, getTagForResponsesStatus, getTagForRuntimeStates } from "../Common/Tags";
import VersionizedObjectsTab from "../Common/VersionizedObjectsTab";
import FaIcon from "../Icons/FaIcon";
import ChangeInstancesTab from "./SubComponents/ChangeInstancesTab";
import Declarations from "./SubComponents/Declarations";
import Metadata from "./SubComponents/Metadata";
import RoutableTabs from "../Common/RoutableTabs";
import { formatDateAndTime, formatDateAndTimeFromNow } from "@src/helpers/utils";
import PackPipelineDetail from "@src/components/AIProcessors/SubComponents/PackPipelineDetail";
import useGetGeneralSettings from "@src/providers/useGetGeneralSettings";

const { Text, Title } = Typography;


const ServiceItemDetailCard = ({ pov, serviceItemsResource, changeInstancesResource, deployedItemsResource, healthcheckResource, healthcheckResponsesResource }: ServiceItemDetailProps) => {
    const { record, loading } = useEditContext<any>();
    const [healthCheckTriggered, setHealthCheckTriggered] = useState(false)
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const { permissions } = usePermissions();
    const notify = useNotify();
    const generalSettings = useGetGeneralSettings();
    const AIIntegrationSettings = generalSettings.find(setting => setting.name == 'AIIntegration')

    const triggerHealthcheckForServiceItem = (record: any) => {
        setHealthCheckTriggered(true);
        dataProvider.getAction(healthcheckResource, { verb: "trigger/service_item/" + record.id })
            .then((response: any) => {
                notify('Healthcheck triggered successfully')
                setHealthCheckTriggered(false)
            }).catch((error: any) => {
                notify('Error triggering healthcheck', 'error')
                setHealthCheckTriggered(false)
            })
    }
    const getMessageForServiceState = (state: string) => {
        if (state === "NO_NEW_SERVICE_ITEM")
            return <>The Service {<Text strong>{record?.service.name}</Text>} is no longer accepting new Service Items. Consumers can still modify or delete existing Service Items.</>

        else if (state === "NO_MODIFY_EXISTING_SERVICE_ITEM")
            return <>Consumers can only decommission existing service items for the {<Text strong>{record?.service.name}</Text>} service. Creation and modification are not allowed.</>

        else if (state === "DECOMMISSIONED")
            return <>The Service {<Text strong>{record?.service.name}</Text>} has been fully decommissioned and is no longer available.</>
    }

    const permissionChangeState = ["ADMIN", "GENERAL"].includes(permissions);

    if (loading || !record) return null;

    return (
            <Card style={{ padding: '8px' }} className=" header-solid h-full ant-card pt-0">
                <Row>
                    {
                        record.is_service_private?
                            <Alert
                                message="Service has become private"
                                description="The service has become private and your team is not in the service's allowed teams list"
                                type="error"
                                style={{ marginBottom: "5px", width: "100%" }}
                            />:<></>
                    }
                    {
                        record.service.state != "IN_SERVICE"?
                        <Alert
                            message={getMessageForServiceState(record.service.state)}
                            type="warning"
                            showIcon
                            style={{marginBottom: "5px", width: "100%"}}
                        />:<></>
                    }
                </Row>
                <Row>
                    <Col
                        style={{ display: 'flex', justifyContent: 'flex-end' }}
                        span={24}
                    >
                        <Space align="center">
                            {record.created && <Tooltip title={formatDateAndTime(record.created)}><Text className='text-sm'>Created {formatDateAndTimeFromNow(record.created)}</Text></Tooltip>}
                        </Space>
                    </Col>
                </Row>
                <Row>
                    <Col span={8}>
                        <Title level={3}>{record.name}</Title>
                    </Col>
                    <Col
                        style={{ display: 'flex', justifyContent: 'flex-end' }}
                        span={16}
                    >
                        <Space>
                            {getTagForRuntimeStates(record.runtime_state)}
                            {getTagForChangeState(record.change_state)}
                        </Space>
                    </Col>
                </Row>
                <Row>
                    <Col style={{ paddingRight: '6px' }}>
                        <Row gutter={2} align="middle">
                            <Col style={{ display: 'flex' }} className='text-sm'>{FaIcon(faRocket)}</Col>
                            <Col>
                                {
                                    record.is_deprecated_service_schema ?
                                        <Tooltip title="The service has a new schema" color={"orange"} placement={"topRight"}>
                                            <Typography.Text className='text-sm' type={'secondary'}>{record.service.name}</Typography.Text>
                                            {" "}
                                            <ExclamationCircleFilled style={{ color: 'orange' }} />
                                        </Tooltip>
                                        :
                                        <Typography.Text className='text-sm' type={'secondary'}>{record.service.name}</Typography.Text>

                                }
                            </Col>
                        </Row>
                    </Col>

                    <Col style={{ paddingLeft: '6px', paddingRight: '6px' }}>
                        <Row gutter={2} align="middle">
                            <Col style={{ display: 'flex' }} className='text-sm'>{FaIcon(faThLarge)}</Col>
                            <Col className='text-sm'><Typography.Text type={'secondary'}>{record.application.name}</Typography.Text></Col>
                        </Row>
                    </Col>
                    {
                        pov === "serviceowner" &&
                        <>
                            <Col style={{ paddingLeft: '6px' }}>
                                <Row gutter={2} align="middle">
                                    <Col style={{ display: 'flex' }} className='text-sm'>{FaIcon(faUsers)}</Col>
                                    <Col className='text-sm'><Typography.Text type={'secondary'}>{record.consumer_team.name}</Typography.Text></Col>
                                </Row>

                            </Col>
                        </>
                    }

                </Row>

                {
                    record?.service?.healthcheck &&
                    <Row>
                        <Col style={{ textAlign: 'right' }} flex='auto'>
                            <Space style={{ textAlign: 'right' }} >
                                <Tooltip title="Last Healthcheck Response">
                                    {getTagForResponsesStatus(record.healthcheck_status)}
                                </Tooltip>
                                {
                                    permissionChangeState &&
                                    <Tooltip title="Trigger Healthcheck">
                                        <Button size="small" type="text" loading={healthCheckTriggered} onClick={() => triggerHealthcheckForServiceItem(record)} icon={<SyncOutlined />} />
                                    </Tooltip>
                                }
                            </Space>
                        </Col>
                    </Row>
                }
                <Row gutter={16} style={{ paddingTop: '10px' }}>
                    <Col className="gutter-row" span={24}>
                        <Card>
                            {(() => {
                                const handleTabChange = (activeKey: string) => {
                                    // console.log('Active tab changed to:', activeKey);
                                };

                                const tabs: RoutableTabConfig[] = [
                                    {
                                        key: 'declarations',
                                        tab: "Declarations",
                                        content: <Declarations resource={serviceItemsResource} service_item={record}/>,
                                    },
                                    {
                                        key: 'change_instances',
                                        tab: "Change Instances",
                                        content: (
                                            <ChangeInstancesTab
                                                resource={changeInstancesResource}
                                                baseFilter={{ service_item_id: record.id }}
                                                submissionPageRedirectVisible={pov === "consumer"}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'deployed_items',
                                        tab: "Deployed Items",
                                        content: (
                                            <VersionizedObjectsTab
                                                resource={deployedItemsResource}
                                                light={true}
                                                name={"deployed item"}
                                                attrName={"data"}
                                                isCreate={pov === "serviceowner" && permissions === "ADMIN"}
                                                record={record}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'metadata',
                                        tab: "Metadata",
                                        content: <Metadata record={record} />,
                                    },
                                ];

                                // Add healthcheck responses tab conditionally
                                if (record?.service?.healthcheck)
                                    tabs.push({
                                        key: 'healthcheck_responses',
                                        tab: "Healthcheck Responses",
                                        content: (
                                            <ResponseListView
                                                columns={HealthCheckResponsesColumns}
                                                resource_name={healthcheckResponsesResource}
                                                filters={{ service_item_id: record.id }}
                                            />
                                        ),
                                    })
                                if (pov==="serviceowner" && AIIntegrationSettings?.config?.show_ai_processor && record?.has_ai_processors)
                                    tabs.push({
                                        key: 'netorca_pack',
                                        tab: "NetOrca Pack",
                                        content: <PackPipelineDetail service_item_id={record.id} />,
                                    })

                                return <RoutableTabs tabs={tabs} defaultActiveKey="declarations" onTabChange={handleTabChange}/>;
                            })()}
                        </Card>
                    </Col>
                </Row>
            </Card>
    )
}

export default ServiceItemDetailCard;
